// @flow
import * as React from 'react';
import deepEqual from 'react-fast-compare';
import cloneDeep from 'lodash.clonedeep';
import { arrToObject, zip, updateIn, partial, getIn } from 'utils/functional';
import scrollIntoView from 'utils/scrollIntoView';
import { FormContextProvider } from './FormContext';

const scrollToError = (isModal?: boolean) => {
  const firstError = document.querySelector('.error');
  if (firstError) {
    scrollIntoView(firstError, {
      padding: 32,
      container: isModal ? 'idealist-modal-container' : undefined
    });
  }
};

// support nested form attributes by parsing dot notation in field names
// e.g. "profile.description"
const getValue = (values, name: string) => getIn(values, name.split('.'));

const updateField = (prev, name: string, updater) =>
  updateIn(prev, name.split('.'), updater);

type Props = {
  initialValues: $ReadOnly<Object>,
  id?: string,
  errors?: APIErrors,
  noScrollToError?: boolean,
  hasStickyHeader?: boolean,
  headerOffset?: number,
  mapError: ({
    name: string,
    value: ?Object,
    type: ?string,
    description: string,
    data: Object
  }) => React.Node,
  onSubmit: ({
    clearErrors: (?Array<string>) => void,
    values: Object,
    updateValues: (update: Object) => void,
    updateSubmitting: boolean => void,
    handleError: (error: Object, updatedFormData: ?Object) => void,
    setErrors: APIErrors => void
  }) => ?Promise<Object>,
  render: ({
    values: Object,
    getValue: string => mixed,
    hasErrors: boolean,
    updateValue: (name: string, value: any, callback?: Function) => void,
    updateValues: Object => void,
    updateValueWith: (
      name: string,
      f: (mixed) => mixed,
      callback?: Function
    ) => void,
    setErrors: APIErrors => void,
    submitting: boolean,
    submitDisabled: boolean,
    updateSubmitDisabled: boolean => void,
    errors?: APIErrors,
    submitForm: () => void,
    handleError: (error: Object, updatedFormData: ?Object) => void,
    clearErrors: (?Array<string>) => void
  }) => React.Node,
  'data-qa-id'?: string,
  className?: string,
  component?: React.ElementType,
  formRef?: { current: null | React.ElementRef<React.ElementType> },
  isModal?: boolean
};

type State = {
  values: Object,
  errors: APIErrors,
  submitting: boolean,
  submitDisabled: boolean,
  fetchingAddress: boolean,
  prevPropsInitialValues: Object,
  edited: boolean
};

export default class FormContainer extends React.Component<Props, State> {
  static defaultProps = {
    // eslint-disable-next-line react/default-props-match-prop-types
    mapError: ({ description }: { description: string }) => description,
    // eslint-disable-next-line react/default-props-match-prop-types
    initialValues: {}
  };

  state = {
    // deep clone to avoid mutating state
    values: cloneDeep(this.props.initialValues),
    errors: this.props.errors || {},
    submitting: false,
    submitDisabled: false,
    fetchingAddress: false,
    // eslint-disable-next-line react/no-unused-state
    prevPropsInitialValues: this.props.initialValues,
    edited: false
  };

  onFetchingAddressComplete: ?() => void;

  // TODO(Karan): do we need this? remove it otherwise
  // See https://www.pivotaltracker.com/story/show/169410492
  // for bug caused by this. This code assumes that all fields in a form
  // will be keys in `this.props.initialValues`
  static getDerivedStateFromProps(props: Props, state: State) {
    const initialValuesChanged = !deepEqual(
      props.initialValues,
      state.prevPropsInitialValues
    );
    const currentFields = Object.keys(state.values);
    const newFields = Object.keys(props.initialValues).filter(
      f => !currentFields.includes(f)
    );
    const hasNewFields = newFields.length > 0;
    const newValues = newFields.reduce((memo, formField) => {
      memo[formField] = props.initialValues[formField];
      return memo;
    }, {});

    if (!initialValuesChanged) {
      if (!hasNewFields) {
        return null;
      }
      return {
        values: {
          ...state.values,
          ...newValues
        }
      };
    }

    // if initialValues is updated and the user hasn't changed the updated
    // form fields, update the field's value
    const updatedFormData = Object.keys(props.initialValues).reduce(
      (memo, formField) => {
        const prevDefaultValue = state.prevPropsInitialValues[formField];
        const newDefaultValue = props.initialValues[formField];
        const currentValue = state.values[formField];

        if (
          !deepEqual(prevDefaultValue, newDefaultValue) &&
          deepEqual(currentValue, prevDefaultValue)
        ) {
          // deep clone to avoid mutating state
          memo[formField] = cloneDeep(newDefaultValue);
        } else {
          memo[formField] = currentValue;
        }
        return memo;
      },
      newValues
    );

    return {
      values: updatedFormData,
      prevPropsInitialValues: props.initialValues
    };
  }

  componentDidUpdate() {
    if (!this.state.fetchingAddress && this.onFetchingAddressComplete) {
      this.onFetchingAddressComplete();
      this.onFetchingAddressComplete = null;
    }
  }

  updateValue = (name: string, newValue: any, callback?: Function) =>
    this.updateValueWith(name, () => newValue, callback);

  // updateValueWith
  // single field with updater function (e.g., incrementing):
  // updateValueWith('field1', oldField1 => oldField1 + 1)
  // multiple fields at once (e.g., incrementing):;
  // updateValueWith(['field1', 'field2'], ({field1, field2}) => {field1: field1 + 1, field2: field2 + 1})
  updateValueWith = (
    name: string | $ReadOnlyArray<string>,
    update: mixed => mixed,
    callback?: Function
  ) => {
    this.setState(prevState => {
      let updatedValues;
      if (typeof name === 'string') {
        updatedValues = updateField(prevState.values, name, update);
      } else {
        const updaterArg = arrToObject(
          zip(name, name.map(partial(getValue, prevState.values)))
        );
        const newValues = update(updaterArg);
        updatedValues = Object.entries(newValues).reduce(
          // $FlowFixMe: annoying tuple / array incompatibility
          (acc, [field, val]) => updateField(acc, field, () => val),
          prevState.values
        );
      }
      return {
        values: updatedValues,
        edited: prevState.edited || !deepEqual(prevState.values, updatedValues)
      };
    }, callback);
  };

  updateValues = (update: Object) => {
    this.updateValueWith(Object.keys(update), () => update);
  };

  updateSubmitting = (submitting: boolean) => {
    this.setState({ submitting });
  };

  updateSubmitDisabled = (submitDisabled: boolean) => {
    this.setState({ submitDisabled });
  };

  clearErrors = (names: ?Array<string>) => {
    this.setState(state => {
      if (!(names && names.length)) {
        return { errors: {} };
      }
      const newErrors = state.errors;
      names.forEach(name => {
        delete newErrors[name];
      });
      return { errors: newErrors };
    });
  };

  handleError = (error: Object, updatedFormData: ?Object = null) => {
    const errorArray = error.responseJson && error.responseJson.errors;
    if (!errorArray) throw error;

    const errors = errorArray.reduce(
      (acc, { name, type, description, data }) => {
        acc[name] = this.props.mapError({
          name,
          value: this.state.values[name],
          type,
          description,
          data: data || {}
        });
        return acc;
      },
      {}
    );

    const updatedState = {};
    updatedState.submitting = false;
    updatedState.errors = errors;
    if (updatedFormData) {
      updatedState.values = {
        ...this.state.values,
        updatedFormData
      };
    }
    this.setState(updatedState);
    if (!this.props.noScrollToError) {
      scrollToError(this.props.isModal);
    }
  };

  setErrors = (errors: APIErrors) => {
    this.setState({
      submitting: false,
      errors
    });
    if (!this.props.noScrollToError) {
      scrollToError(this.props.isModal);
    }
  };

  get hasErrors() {
    return Object.keys(this.state.errors).length > 0;
  }

  setFetchingAddress = (fetchingAddress: boolean) => {
    this.setState({ fetchingAddress });
  };

  submitForm = () => {
    this.setState(prevState => ({
      submitting: true,
      // reset the errors on form submission
      errors: this.props.errors ? prevState.errors : {}
    }));
    const performSubmit = () => {
      if (this.props.onSubmit) {
        this.props.onSubmit({
          values: this.state.values,
          updateValues: this.updateValues,
          updateSubmitting: this.updateSubmitting,
          clearErrors: this.clearErrors,
          handleError: this.handleError,
          setErrors: this.setErrors,
          setFetchingAddress: this.setFetchingAddress
        });
      }
    };

    if (this.state.fetchingAddress) {
      this.onFetchingAddressComplete = performSubmit;
    } else {
      performSubmit();
    }
  };

  render() {
    const FormComponent = this.props.component || 'form';
    return (
      <FormContextProvider
        value={{
          values: this.state.values,
          getValue: partial(getValue, this.state.values),
          updateValue: this.updateValue,
          updateValues: this.updateValues,
          updateValueWith: this.updateValueWith,
          setFetchingAddress: this.setFetchingAddress,
          errors: this.state.errors,
          setErrors: this.setErrors,
          clearErrors: this.clearErrors,
          submitting: this.state.submitting,
          submitDisabled: this.state.submitDisabled,
          updateSubmitDisabled: this.updateSubmitDisabled,
          fieldQaIdPrefix: this.props['data-qa-id'],
          edited: this.state.edited,
          handleError: this.handleError
        }}
      >
        <FormComponent
          ref={this.props.formRef}
          data-qa-id={this.props['data-qa-id']}
          onSubmit={e => {
            e.preventDefault();
            this.submitForm();
          }}
          className={this.props.className}
          id={this.props.id}
        >
          {this.props.render({
            values: this.state.values,
            getValue: partial(getValue, this.state.values),
            updateValue: this.updateValue,
            updateValueWith: this.updateValueWith,
            updateValues: this.updateValues,
            hasErrors: this.hasErrors,
            setErrors: this.setErrors,
            submitting: this.state.submitting,
            submitDisabled: this.state.submitDisabled,
            updateSubmitDisabled: this.updateSubmitDisabled,
            errors: this.state.errors,
            submitForm: this.submitForm,
            edited: this.state.edited,
            handleError: this.handleError,
            clearErrors: this.clearErrors
          })}
        </FormComponent>
      </FormContextProvider>
    );
  }
}
